import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Textarea } from "~/components/ui/textarea";

export interface EscalatePatientDialogProps {
  /** The patient being escalated (name + bed for the confirm copy). */
  patient: { fullName: string; bedNumber: string };
  open: boolean;
  /** Mirrors Radix Dialog's onOpenChange; suppressed while submitting. */
  onOpenChange: (open: boolean) => void;
  /**
   * Called when the user confirms, with the (optional, trimmed by the caller)
   * reason. The dialog shows a spinner until this settles; on resolve it closes,
   * on reject it surfaces a toast and stays open so the user can retry. The API
   * call lives in the parent.
   */
  onConfirm: (reason: string) => Promise<void>;
}

/**
 * HAN-80 (V2-E5.3) — confirm dialog before escalating a whole patient to a
 * senior clinician (whole-patient emergency), with an optional free-text reason.
 * Mirrors the task-escalate dialog's submit/loading posture.
 */
export function EscalatePatientDialog({
  patient,
  open,
  onOpenChange,
  onConfirm,
}: EscalatePatientDialogProps): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState("");

  const handleConfirm = async (): Promise<void> => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(reason);
      setReason("");
      onOpenChange(false);
    } catch {
      toast.error("Couldn't escalate this patient. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent
        onEscapeKeyDown={(e) => {
          if (submitting) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (submitting) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Escalate patient to a senior</DialogTitle>
          <DialogDescription className="text-foreground">
            The most-senior available clinician on this ward will be notified with this
            patient&apos;s details. Add an optional reason for the record.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Patient: {patient.fullName} (Bed {patient.bedNumber})
        </p>

        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          rows={3}
          aria-label="Reason for escalation (optional)"
          placeholder="Reason (optional)"
        />

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={submitting}
            data-testid="escalate-patient-confirm"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Escalating…
              </>
            ) : (
              "Escalate patient"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
